import { useTheme } from '../theme/ThemeProvider';
import { useMemo } from 'react';
import tokens from './tokens.json';
import type { Tokens } from './types';

/**
 * Hook to get all design tokens for the current theme
 * @returns Complete token object for the active theme
 */
export const useTokens = (): Tokens => {
  const { theme } = useTheme();

  return useMemo(() => {
    // Fall back to light theme if tokens don't exist for the current theme
    const themeTokens = tokens[theme as keyof typeof tokens] || tokens.light;
    return themeTokens as unknown as Tokens;
  }, [theme]);
};

/**
 * Hook to get color tokens for the current theme
 * @returns Color token object with all semantic color values
 */
export const useColorTokens = () => {
  const tokens = useTokens();
  return tokens.colors;
};

/**
 * Hook to get spacing tokens
 * @returns Spacing scale object with rem/px values
 */
export const useSpacingTokens = () => {
  const tokens = useTokens();
  return tokens.spacing;
};

/**
 * Hook to get typography tokens
 * @returns Typography scale with font sizes, weights, and line heights
 */
export const useTypographyTokens = () => {
  const tokens = useTokens();
  return tokens.typography;
};

/**
 * Hook to get animation tokens
 * @returns Animation duration and easing values
 */
export const useAnimationTokens = () => {
  const tokens = useTokens();
  return tokens.animation;
};

/**
 * Hook to get shadow tokens
 * @returns Box shadow definitions for different elevations
 */
export const useShadowTokens = () => {
  const tokens = useTokens();
  return tokens.shadows;
};

/**
 * Hook to get border radius tokens
 * @returns Border radius values for different corner styles
 */
export const useBorderRadiusTokens = () => {
  const tokens = useTokens();
  return tokens.borderRadius;
};

/**
 * Hook to get a specific color token value
 * @param colorName - The color token name (e.g., 'primary', 'base-100')
 * @returns The hex color value for the current theme
 */
export const useColorValue = (colorName: keyof Tokens['colors']) => {
  const colors = useColorTokens();
  return colors[colorName];
};

/**
 * Hook to get a specific spacing token value
 * @param spacing - The spacing token key (e.g., '4', '8', '16')
 * @returns The spacing value in rem/px
 */
export const useSpacingValue = (spacing: keyof Tokens['spacing']) => {
  const spacingTokens = useSpacingTokens();
  return spacingTokens[spacing];
};
